import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { colorCodesForAvatar } from "../../../../constants/index";
import { chracterPosition } from "../../../../utils/index";
import { CustomerSidebarSkeleton } from "./CustomerLoadingSkeletons";
import { peopleEllipsisSx } from "../peopleFormValidation";
import { getCustomerConversationHistory } from "../../../../store/features/getCustomerConversationHistory";
import { mapConversationsToRows } from "../../../../utils/conversationHistoryMappers";
import ConversationsHistoryModal from "../../TicketV2/ConversationsHistoryModal";
import { HistoryPreviewSkeleton } from "../../TicketV2/ticketDetails/TicketDetailsSelects";
import { IconInbox16 } from "../../TicketV2/TicketV2Icons";

const inter = '"Inter", sans-serif';

const sidebarCloseButtonSx = {
  p: 0,
  flexShrink: 0,
  overflow: "visible",
  // 24px canvas vs 16px chevrons — nudge so right edges line up without clipping
  mr: "-4px",
  "& svg": {
    width: 24,
    height: 24,
    display: "block",
    overflow: "visible",
  },
};

const emptyHintSx = {
  color: "#93A1B0",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
};

function DetailChip({ label }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "100%",
        minWidth: 0,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
        color: "#405261",
        fontFamily: "Inter",
        fontSize: 12,
        fontWeight: 400,
        height: '32px',
        lineHeight: "18px",
      }}
    >
      <Typography
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          fontSize: "inherit",
          lineHeight: "inherit",
        }}
        title={label || undefined}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ borderBottom: "1px solid #EEF2F6", py: 1.5 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "18px",
          }}
        >
          {title}
        </Typography>
        <Dropdown
          style={{
            width: 16,
            height: 16,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      {open && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Box>
  );
}

function DetailField({ label, value }) {
  return (
    <Box sx={{ mb: '16px' }}>
      <Typography
        sx={{
          color: "#405261",
          fontFamily: "Inter",
          fontSize: 12,
          fontWeight: 400,
          lineHeight: "18px",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#061522",
          fontFamily: "Inter",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "18px",
          ...peopleEllipsisSx,
        }}
        title={value || undefined}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function CustomerDetailSidebar({
  customer,
  loading = false,
  onClose,
  onViewDetail,
  onEdit,
  canEdit = true,
}) {
  const dispatch = useDispatch();
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);
  const [chatHistoryPreview, setChatHistoryPreview] = useState([]);
  const [chatHistoryTotal, setChatHistoryTotal] = useState(0);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);

  const historyCustomerId =
    customer?.id ||
    customer?._id ||
    customer?.raw?.id ||
    customer?.raw?._id ||
    "";
  const historyCustomerName = customer?.name || "Customer";

  useEffect(() => {
    if (!historyCustomerId) {
      setChatHistoryPreview([]);
      setChatHistoryTotal(0);
      setChatHistoryLoading(false);
      return undefined;
    }
    if (showChatHistoryModal) return undefined;

    let cancelled = false;
    setChatHistoryLoading(true);
    dispatch(
      getCustomerConversationHistory({
        customerId: historyCustomerId,
        page: 1,
        limit: 5,
      })
    )
      .then((res) => {
        if (cancelled) return;
        if (res.meta.requestStatus === "fulfilled") {
          const data = res.payload?.data || {};
          setChatHistoryPreview(mapConversationsToRows(data.conversations));
          setChatHistoryTotal(data.summary?.total || data.pagination?.total || 0);
        } else {
          setChatHistoryPreview([]);
          setChatHistoryTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setChatHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, historyCustomerId, showChatHistoryModal]);

  useEffect(() => {
    setChatHistoryPreview([]);
    setChatHistoryTotal(0);
  }, [historyCustomerId]);

  if (!customer && !loading) return null;

  const letter = (customer?.name || "").substring(0, 1).toUpperCase();
  const tags = customer?.tags || [];

  return (
    <>
      <ConversationsHistoryModal
        isOpen={showChatHistoryModal}
        onClose={() => setShowChatHistoryModal(false)}
        customerId={historyCustomerId}
        customerName={historyCustomerName}
      />

      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(6, 21, 34, 0.2)",
          zIndex: 1200,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 280,
          height: "100vh",
          bgcolor: "#FFF",
          boxShadow: "0 4px 80px 0 rgba(6, 21, 34, 0.10)",
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
          fontFamily: "Inter",
        }}
      >
        {loading ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 2, flexShrink: 0 }}>
              <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                <CloseIcon />
              </IconButton>
            </Box>
            <CustomerSidebarSkeleton />
          </>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
              {!customer ? (
                <Box sx={{ py: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#405261" }}>
                    Unable to load customer details.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      py: 2,
                      minWidth: 0,
                    }}
                  >
                    {customer.avatar ? (
                      <Avatar
                        src={customer.avatar}
                        alt={customer.name}
                        sx={{ width: 40, height: 40, flexShrink: 0 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: 14,
                          flexShrink: 0,
                          bgcolor:
                            colorCodesForAvatar[chracterPosition(letter)] || "#405261",
                        }}
                      >
                        {letter}
                      </Avatar>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#061522",
                            fontFamily: "Inter",
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: "18px",
                            flex: 1,
                            minWidth: 0,
                            ...peopleEllipsisSx,
                          }}
                          title={customer.name}
                        >
                          {customer.name}
                        </Typography>
                        <IconButton onClick={onClose} size="small" sx={sidebarCloseButtonSx}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                      <Typography
                        sx={{
                          color: "#405261",
                          fontFamily: "Inter",
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "18px",
                          mt: 0.25,
                          ...peopleEllipsisSx,
                        }}
                        title={customer.company || undefined}
                      >
                        {customer.company || "—"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderBottom: "1px solid #E5E9EC",
                      mx: -2,
                      flexShrink: 0,
                    }}
                  />

          <CollapsibleSection title="Customer Information">
            <DetailField label="Email" value={customer.email} />
            <DetailField label="Phone" value={customer.phone} />
            <DetailField
              label="Last Seen"
              value={customer.lastSeenDate || customer.lastSeen || customer.lastActivity}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Groups">
            <Box>
              <Typography
                sx={{
                  color: "#405261",
                  fontFamily: "Inter",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  mb: 0.75,
                }}
              >
                Groups
              </Typography>
              <DetailChip label={customer.group} />
            </Box>
          </CollapsibleSection>

          <CollapsibleSection title="Tags">
            {tags.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {tags.map((tag) => (
                  <DetailChip key={tag} label={tag} />
                ))}
              </Box>
            ) : (
              <Typography sx={emptyHintSx}>No tags</Typography>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="History">
            {chatHistoryLoading && chatHistoryPreview.length === 0 ? (
              <HistoryPreviewSkeleton rows={5} />
            ) : chatHistoryPreview.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {chatHistoryPreview.map((row) => (
                  <Box
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowChatHistoryModal(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowChatHistoryModal(true);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "#F9FAFA" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconInbox16 />
                    </Box>
                    <Typography
                      sx={{
                        color: "#405261",
                        fontFamily: inter,
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        flex: 1,
                        minWidth: 0,
                        ...peopleEllipsisSx,
                      }}
                      title={row.subject}
                    >
                      {row.subject}
                    </Typography>
                  </Box>
                ))}
                <Typography
                  component="button"
                  type="button"
                  onClick={() => setShowChatHistoryModal(true)}
                  sx={{
                    mt: 0.5,
                    p: 0,
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: inter,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: "#148DFD",
                    textDecoration: "underline",
                    textDecorationSkipInk: "none",
                    textUnderlineOffset: "4px",
                    "&:hover": { color: "#0B7AD9" },
                  }}
                >
                  View All {chatHistoryTotal}
                </Typography>
              </Box>
            ) : (
              <Typography sx={emptyHintSx}>No History</Typography>
            )}
          </CollapsibleSection>
                </>
              )}
            </Box>

            {customer && (
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  flexShrink: 0,
                  display: "flex",
                  gap: "8px",
                }}
              >
                <Button
                  onClick={() => onViewDetail?.()}
                  sx={{
                    width: canEdit ? 160 : "100%",
                    minWidth: canEdit ? 160 : "100%",
                    height: 40,
                    borderRadius: "8px",
                    background: "#148DFD",
                    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                    color: "#FFF",
                    textAlign: "center",
                    fontFamily: "Inter",
                    fontSize: 16,
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "18px",
                    textTransform: "none",
                    flexShrink: 0,
                    "&:hover": { background: "#0077CC" },
                  }}
                >
                  View Detail
                </Button>
                {canEdit ? (
                <Button
                  onClick={() => onEdit?.()}
                  sx={{
                    width: 80,
                    minWidth: 80,
                    height: 40,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                    color: "#061522",
                    textAlign: "center",
                    fontFamily: "Inter",
                    fontSize: 16,
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "18px",
                    textTransform: "none",
                    flexShrink: 0,
                    "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
                  }}
                >
                  Edit
                </Button>
                ) : null}
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
}
